import { BaseWebComponentController, IListenerConfig } from '../../../../framework/BaseWebComponentController';
import { LicenseMonthlyServicesFormWebComponent } from '../component/LicenseMonthlyServicesFormWebComponent';
import { LicenseMonthlyServicesFormView } from '../view/LicenseMonthlyServicesFormView';
import { templateLicenseMonthlyServicesForm } from '../view/template';
import { ToggleEvents } from '../../../../ui/gzToggle/types';
import { Feature, FeatureFlag } from '@nirvana/shared/constants';
import { MonthlyProductType } from '../../../types/MonthlyProductType';
import { ServicesFormEvent } from '../types/ServicesFormEvent';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { CompanyFeatures, ProductService } from '../../../../company/types/company-data';
import { ProductServiceType } from '../../../types/ProductServiceType';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { InputEvents } from '../../../../ui/gzInput/types';
import { ProductServiceStatus } from '../../../types/ProductServiceStatus';
import { CompanyLicenseInformation } from '../../../types/CompanyLicenseInformation';
export class LicenseMonthlyServicesFormController extends BaseWebComponentController {
  public component!: LicenseMonthlyServicesFormWebComponent;
  public view!: LicenseMonthlyServicesFormView;
  public viewConstructor!: typeof LicenseMonthlyServicesFormView;

  private usableProduct: MonthlyProductType | undefined;
  private availableFeatures: Feature[] | undefined;
  private productServices: ProductService[] | undefined;
  private hiddenServices: string[] = [];
  private featuresInTrial: FeatureFlag[];
  private parentCompany: CompanyLicenseInformation['parentCompany'] | undefined;

  constructor(component: LicenseMonthlyServicesFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicenseMonthlyServicesFormView,
      viewTemplate: templateLicenseMonthlyServicesForm,
    });
  }

  public setUsableProduct(usableProduct: MonthlyProductType | undefined) {
    this.usableProduct = usableProduct;

    this.showCompatibleServices();
  }

  public setAvailableFeatures(availableFeatures: Feature[] | undefined) {
    this.availableFeatures = availableFeatures;

    this.showCompatibleServices();
  }

  public setProductServices(productServices: ProductService[]) {
    if (productServices === undefined) {
      return;
    }

    this.productServices = productServices;

    // Update MDR status in frontend after setting the productServices array
    const status = productServices.find(productService => productService.type === ProductServiceType.MDR)?.status;

    if (status) {
      this.view.showMdrStatus(status);
    }
  }

  public setFeaturesInTrial(featuresInTrial: FeatureFlag[] | undefined): void {
    this.featuresInTrial = featuresInTrial ? featuresInTrial : [];
  }

  public setParentCompany(parentCompany: CompanyLicenseInformation['parentCompany'] | undefined): void {
    this.parentCompany = parentCompany;
  }

  public updateToggles(features: CompanyFeatures) {
    this.setUsableProduct(MonthlyProductType.getAssignedProduct(features));

    if (features[FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE]) {
      this.view.dom.$managedDetectionAndResponseFlavours.selectedOption = this.view.mdrAdviceUsable;
      this.view.dom.$managedDetectionAndResponseToggle.setFormValue(true);
      this.view.mdrAdviceFormField.value = true;
    } else {
      this.view.dom.$managedDetectionAndResponseToggle.setFormValue(false);
    }

    const parentMdrServiceStatus = this.parentCompany ? this.parentCompany.mdrManagerStatus : null;
    const parentMdrContactInfoRequired =
      !this.view.mdrAdviceFormField.value &&
      parentMdrServiceStatus !== ProductServiceStatus.ACTIVE &&
      parentMdrServiceStatus !== ProductServiceStatus.ONBOARDING;

    toggleVisibility(this.view.dom.$parentCompanyMdrContactInfoRequired, parentMdrContactInfoRequired);

    this.view.dom.$managedDetectionAndResponseToggle.disabled =
      parentMdrContactInfoRequired ||
      this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE] ||
      this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE];

    toggleVisibility(
      this.view.dom.$mdrInTrialText,
      this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE]
    );
  }

  private showCompatibleServices(): void {
    let isAnyServiceAvailable = false;
    let isAnyServiceAvailableForResell = false;

    if (this.usableProduct === undefined || !(this.usableProduct in MonthlyProductType)) {
      this.component.dispatchEvent(
        new CustomEvent(ServicesFormEvent.ViewUpdate, {
          detail: {
            isAnyServiceAvailable,
            isAnyServiceAvailableForResell,
          },
          bubbles: true,
          composed: true,
        })
      );

      return;
    }

    isAnyServiceAvailable = this.hasServicesAvailable() && !this.hiddenServices.includes('mdr-usable');
    isAnyServiceAvailableForResell = this.hasServicesAvailable() && !this.hiddenServices.includes('mdr-delegate');
    toggleVisibility(this.view.dom.$managedDetectionAndResponseToggle, isAnyServiceAvailable);
    toggleVisibility(this.view.dom.$managedDetectionAndResponseFlavours, isAnyServiceAvailable);

    this.component.dispatchEvent(
      new CustomEvent(ServicesFormEvent.ViewUpdate, {
        detail: {
          isAnyServiceAvailable,
          isAnyServiceAvailableForResell,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$managedDetectionAndResponseToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onMdrToggleClick,
      },
      {
        element: this.view.dom.$managedDetectionAndResponseFlavours,
        event: InputEvents.CHANGE,
        callback: this.onMdrFlavourChange,
      },
    ];
  }

  private onMdrToggleClick(): void {
    const mdrValue = this.view.dom.$managedDetectionAndResponseToggle.getFormValue();

    if (mdrValue) {
      this.view.dom.$managedDetectionAndResponseFlavours.setEnabled(true, false);
      this.view.dom.$mdrAdviceFlavour.disabled = false;

      if (isEmpty(this.view.dom.$managedDetectionAndResponseFlavours.selectedOption)) {
        // when toggling MDR activate Foundations flavour (mdrAdvice) by default
        this.view.dom.$managedDetectionAndResponseFlavours.selectedOption = this.view.mdrAdviceUsable;
        this.view.mdrAdviceFormField.value = true;
      }
    } else {
      this.view.dom.$managedDetectionAndResponseFlavours.setEnabled(false);
      this.view.dom.$managedDetectionAndResponseFlavours.selectedOption = '';
      this.view.mdrAdviceFormField.value = false;
    }

    // create an event when MDR is modified for EDR manipulation
    this.component.dispatchEvent(
      new CustomEvent(ServicesFormEvent.MdrToggleUpdate, {
        detail: {
          mdrValue,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onMdrFlavourChange(): void {
    this.view.mdrAdviceFormField.value =
      this.view.dom.$managedDetectionAndResponseFlavours.selectedOption === this.view.mdrAdviceUsable;
  }

  public hideServices(features: FeatureFlag[]): void {
    this.hiddenServices = [];
    if (features.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE)) {
      this.hiddenServices.push('mdr-usable');
    }

    if (features.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE_DELEGATE)) {
      this.hiddenServices.push('mdr-delegate');
    }

    this.showCompatibleServices();
  }

  /**
   *
   * @param {FeatureFlag[]} features
   * @param {boolean} checkedValue
   */
  public setCheckedValueForServices(features: FeatureFlag[], checkedValue: boolean): void {
    if (features.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE)) {
      this.view.dom.$managedDetectionAndResponseToggle.setFormValue(checkedValue);
    }
  }

  public hasServicesAvailable(): boolean {
    return Feature.isFeatureAvailable(this.availableFeatures || [], Feature.PRODUCT_FEATURE_MDR_ADVICE_USABLE_DELEGATE);
  }
}
